import {registerLocaleData} from '@angular/common';
import {HttpClientModule} from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {APP_INITIALIZER, LOCALE_ID, NgModule} from '@angular/core';
import {MatFormFieldModule, MatListModule, MatSelectModule} from '@angular/material';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouteReuseStrategy} from '@angular/router';
import {ServiceWorkerModule} from '@angular/service-worker';
import {
  AuthModule,
  AuthorizationConfig,
  AuthorizationConfigToken,
  IAuthenticationManagerToken,
  IAuthorizationManagerToken,
  LOG_CONSUMER_SERVICE,
  LogConsoleConsumerService,
  LogModule,
  NoRouteReuseStrategy,
  NotFoundModule,
  OIDC_CONFIG,
  OidcAuthModule,
  OpenIDConnectSettings,
  SEARCH_TOKEN,
  SearchKeepService,
  UikitModule,
  UnauthorizedModule,
  UpdateModule
} from '@cnj/uikit';
import {AkitaNgDevtools} from '@datorama/akita-ngdevtools';
import {BUILD_ENVIRONMENT} from 'src/build/build-environment';
import {AppConfig} from './app-config.model';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {environment} from './environment';
import {SisnorAuthorizationManager} from 'src/auth/sisnor-authorization-manager';
import {SisnorAuthenticationManager} from "../auth/sisnor-authentication-manager";

registerLocaleData(localePt);

export function initializeApp(environmentConfig: environment) {
  return (): Promise<AppConfig> => environmentConfig.load();
}

const configServiceFactory = (): OpenIDConnectSettings => {
  return environment.settings.authentication;
};

const authorizationConfigFactory = (): AuthorizationConfig => {
  const authenticationSettings: AuthorizationConfig = {
    clientId: environment.settings.authentication.client_id,
    // Keycloak é a ferramenta que deixamos configurado por padrão (foi usado o padrão User Managed Access que esta ferramenta utiliza).
    // No entanto, é permitido a utilização de outros meios (e ferramentas) para verificação de autorização.
    // Verifique a documentação para maiores detalhes.
    umaConfig: {
      permissionEndpoint: `${environment.settings.authentication.authority}authz/protection/permission`,
      tokenEndpoint: `${environment.settings.authentication.authority}protocol/openid-connect/token`,
    }
  };
  return authenticationSettings;
};

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    UikitModule,
    MatListModule,
    MatFormFieldModule,
    MatSelectModule,
    LogModule,
    NotFoundModule,
    UnauthorizedModule,
    AuthModule,
    OidcAuthModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: BUILD_ENVIRONMENT.production }),
    BUILD_ENVIRONMENT.production ? [] : [AkitaNgDevtools.forRoot()], // AkitaNgRouterStoreModule.forRoot()
    AppRoutingModule,
    UpdateModule,

  ],
  providers: [
    // Configuração baseado em variáveis de ambiente
    environment,
    // { provide: AuthService, useClass: SisnorAuthenticationService },
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [environment], multi: true },

    { provide: RouteReuseStrategy, useClass: NoRouteReuseStrategy },

    // Configuração de favoritos
    // { provide: FAVORITOS_SERVICE_TOKEN, useClass: AppFavoritosService },

    // Configuração de autenticação (OpenID Connect)
    { provide: OIDC_CONFIG, useFactory: configServiceFactory },
    // Caso não informe o AuthenticationManager, será usado um de OAuth2 puro.
    // No entanto não buscará informações de autorização (no momento da autenticação), pois não existe
    // um padrão de busca de todas as autorizações (isto então foi/deve tratado particularmente em cada ferramenta).
    { provide: IAuthenticationManagerToken, useClass: SisnorAuthenticationManager },
    // { provide: IAuthenticationManagerToken, useClass: Wso2AuthenticationManager },

    // Configuração de autorização (UserBased, UMA ou XACML)
    // Por padrão é utilizado o UMA devido ser o único que funciona "out-of-box" baseado
    // em um padrão suportado pelas ferramentas mais utilizadas de SSO. Porém é recomendado utilizar
    // o UserAuthorizationManager, pois este é offline, não necessitando de requisições
    // a cada verificação ao contrário do UMA ou XACML.
    { provide: IAuthorizationManagerToken, useClass: SisnorAuthorizationManager },
    { provide: AuthorizationConfigToken, useFactory: authorizationConfigFactory },

    // Configuração de log (LogService)
    { provide: LOG_CONSUMER_SERVICE, useClass: LogConsoleConsumerService, multi: true },

    // Serviço de pesquisa para o Layout (Shell) que retorna os itens de menus
    { provide: SEARCH_TOKEN, useClass: SearchKeepService, multi: true },

    { provide: LOCALE_ID, useValue: 'pt-BR' }
  ],
  bootstrap: [AppComponent],
  entryComponents: []
})
export class AppModule { }
